import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { useAuth } from "../../../firebase/AuthContext";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { useLanguageSync } from "../../../hooks/useLanguageSync";
import { ChatInput, BotAvatarIcon } from "../AiChat/components";
import { createChatSession, streamChatMessage } from "./api/chat";
import { ChatSettingsPicker } from "./components/ChatSettingsControls";
import { getApiUrl } from "../../../config/api";

const STAGE_LABEL = {
  understanding: {
    en: "Understanding your question...",
    zh: "正在理解您的問題...",
  },
  searching: { en: "Checking the knowledge base...", zh: "正在查詢知識庫..." },
  stage2_searching: {
    en: "No exact match found — searching the T-Global website and the web outside from TGlobal...",
    zh: "知識庫查無相符結果，正在搜尋 T-Global 網站及外部網路...",
  },
  stage2_partial: {
    en: "Still gathering more information...",
    zh: "正在蒐集更多資訊...",
  },
  synthesizing: {
    en: "Summarizing what we found...",
    zh: "正在彙整搜尋結果...",
  },
  comparing: {
    en: "Comparing the results to find the best answer...",
    zh: "正在比較結果以找出最佳答案...",
  },
  composing: { en: "Composing the final answer...", zh: "正在整理最終答案..." },
};

function randomId() {
  return Math.random().toString(36).slice(2, 10);
}

// ── Conversations are recorded by the FAQ agent ─────────────────────────────
// The page never saves: the FAQ agent records every turn this page sends it
// into History (conversations/{uid}) as the answer streams, a closed tab
// included.

// Saved messages (History's shape) back into this page's message shape.
function toPageMessages(saved) {
  return saved.map((m) => {
    if (m.role === "user") return { id: m.id, role: "user", text: m.message };
    const meta = m.meta || {};
    return {
      id: m.id,
      role: "assistant",
      text: m.message,
      status: "done",
      startedAt: m.timestamp,
      elapsedMs: meta.elapsedMs ?? 0,
      source: meta.source,
      matchedFaqs: meta.matchedFaqs,
      webSources: meta.webSources,
    };
  });
}

// Extract every cited source index out of the answer text. The synthesis prompt
// asks for one marker per source ([1][12]), but the LLM doesn't always comply and
// sometimes emits comma-joined markers like [1, 12, 15] — so this matches both
// forms and pulls out every number inside.
function extractCitedIndexes(text) {
  const indexes = new Set();
  for (const match of text.matchAll(/\[(\d+(?:\s*,\s*\d+)*)\]/g)) {
    for (const n of match[1].split(",")) {
      indexes.add(Number(n.trim()));
    }
  }
  return indexes;
}

function FaqChatbot() {
  const { currentUser } = useAuth();
  const { language, setLanguage } = useLanguageSync();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [verbosity, setVerbosity] = useState("medium");
  const [thinking, setThinking] = useState("balanced");
  const [groundingModel, setGroundingModel] = useState(
    "gemini-3.5-flash-lite",
  );
  const scrollAnchorRef = useRef(null);

  const userId = currentUser?.email ?? "anonymous";

  const startNewChat = async () => {
    setSessionId(null);
    setMessages([]);
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const newSessionId = await createChatSession(token, userId, {
        // Omitting the field when it's already the backend default keeps the
        // request body minimal — the backend falls back to the same value anyway.
        verbosity: verbosity !== "medium" ? verbosity : undefined,
        thinking: thinking !== "balanced" ? thinking : undefined,
        groundingModel:
          groundingModel !== "gemini-3.5-flash-lite"
            ? groundingModel
            : undefined,
      });
      setSessionId(newSessionId);
    } catch (err) {
      console.error("[FaqChatbot] Failed to create chat session:", err);
      setSessionId(null);
    }
  };

  // Reopens a conversation from History: its saved messages, and the agent's
  // session under the same id, so the agent still has the earlier turns as
  // context when it kept them. Falls back to a new chat if it cannot load.
  const continueChat = async (savedSessionId) => {
    setSessionId(null);
    setMessages([]);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${getApiUrl()}/api/v2/history/${savedSessionId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      await createChatSession(token, userId, { sessionId: savedSessionId });
      setMessages(toPageMessages(data.messages || []));
      setSessionId(savedSessionId);
    } catch (err) {
      console.error("[FaqChatbot] Failed to continue the chat:", err);
      startNewChat();
    }
  };

  useEffect(() => {
    // History hands a conversation over through this key (see openInChat).
    const handedOver = currentUser && localStorage.getItem("sessionId");
    if (handedOver) {
      localStorage.removeItem("sessionId");
      continueChat(handedOver);
    } else {
      startNewChat();
    }
    // Open one session on mount; a new chat afterwards is user-triggered via the
    // New Chat button, not a side effect of re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || isBusy || !sessionId || !currentUser) return;

    setInput("");
    setIsBusy(true);

    const userMessage = { id: randomId(), role: "user", text: question };
    const assistantId = randomId();
    const startedAt = Date.now();
    const assistantMessage = {
      id: assistantId,
      role: "assistant",
      text: "",
      status: "loading",
      stage: "understanding",
      startedAt,
    };
    setMessages((prev) => [...prev, userMessage, assistantMessage]);

    const updateAssistant = (patch) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m)),
      );
    };

    const token = await currentUser.getIdToken();

    await streamChatMessage(token, userId, sessionId, question, {
      onStageChange: (stage) => updateAssistant({ stage }),
      onUnderstoodQuery: (understoodQuery) =>
        updateAssistant({ understoodQuery }),
      onSourcesReady: (source, webSources) => {
        updateAssistant({ source, webSources, status: "streaming" });
      },
      onTextDelta: (delta) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, text: m.text + delta, status: "streaming" }
              : m,
          ),
        );
      },
      onDone: ({ source, matchedFaqs, webSources }) => {
        updateAssistant({
          status: "done",
          source,
          matchedFaqs,
          webSources,
          elapsedMs: Date.now() - startedAt,
        });
        setIsBusy(false);
      },
      onError: (error) => {
        updateAssistant({
          status: "error",
          errorText: error.message,
          elapsedMs: Date.now() - startedAt,
        });
        setIsBusy(false);
      },
    });
  };

  if (!currentUser) return null;

  // --- Empty state (no messages yet) ---
  if (messages.length === 0) {
    return (
      <div className="flex min-h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
        <Sidebar />
        <LanguageSwitcher language={language} setLanguage={setLanguage} />

        <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto py-8">
          <h2 className="text-black dark:text-white text-4xl font-bold mb-4 text-center">
            {language === "zh" ? "FAQ 智能客服" : "FAQ Chatbot"}
          </h2>
          <p className="text-gray-800 dark:text-slate-300 text-lg mb-8 text-center max-w-md">
            {language === "zh"
              ? "詢問任何關於散熱產品的問題。"
              : "Ask a question about thermal management products."}
          </p>

          <div className="md:w-3/5 w-11/12 m-5 flex flex-col gap-2 items-center justify-center p-4 rounded-2xl bg-black/10 dark:bg-white/10 border border-black/20 dark:border-white/20 shadow-lg">
            <ChatInput
              input={input}
              setInput={setInput}
              isLoading={isBusy}
              language={language}
              onSubmit={handleSend}
              disabled={!sessionId}
              variant="full"
            />
            <div className="w-full flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <ChatSettingsPicker
                  verbosity={verbosity}
                  onVerbosityChange={setVerbosity}
                  thinking={thinking}
                  onThinkingChange={setThinking}
                  groundingModel={groundingModel}
                  onGroundingModelChange={setGroundingModel}
                  language={language}
                />
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {language === "zh"
                  ? "AI 生成的答案，仍需人工確認"
                  : "AI-generated answers may need human review"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Conversation state (messages present) ---
  return (
    <div className="flex w-screen h-screen bg-slate-100 dark:bg-dashboard-bg text-black dark:text-white font-sans">
      <Sidebar />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="flex-1 h-full flex flex-col pt-16 md:pt-12 transition-all duration-300 min-w-0">
        <div className="w-full h-full flex flex-col items-center justify-center mx-auto px-4 md:px-0 w-[90%]">
          {/* Messages list */}
          <div className="w-full md:w-5/6 h-[70%] md:h-[65%] flex flex-col rounded-xl overflow-y-auto no-scrollbar gap-6 md:gap-12 px-2">
            {messages.map((message) => (
              <div key={message.id} className="w-full">
                <MessageBubble message={message} language={language} />
              </div>
            ))}
            <div ref={scrollAnchorRef} />
          </div>

          {/* Input area */}
          <div className="w-full md:w-[70%] h-auto mx-2 md:mx-5 my-3 md:my-5 flex flex-col gap-2 items-center justify-center p-2 md:p-4 rounded-2xl bg-black/10 dark:bg-white/10 border border-black/20 dark:border-white/20 shadow-lg">
            <ChatInput
              input={input}
              setInput={setInput}
              isLoading={isBusy}
              language={language}
              onSubmit={handleSend}
              onNewChat={startNewChat}
              disabled={!sessionId}
              variant="compact"
            />
            <div className="w-full flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <ChatSettingsPicker
                  verbosity={verbosity}
                  onVerbosityChange={setVerbosity}
                  thinking={thinking}
                  onThinkingChange={setThinking}
                  groundingModel={groundingModel}
                  onGroundingModelChange={setGroundingModel}
                  language={language}
                />
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {language === "zh"
                  ? "AI 生成的答案，仍需人工確認"
                  : "AI-generated answers may need human review"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FaqChatbot;

function MessageBubble({ message, language }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="flex justify-end mb-2 md:mb-4">
        <div
          className="max-w-[85%] md:max-w-[70%] bg-orange-500 text-white p-2 md:p-3 rounded-bl-3xl rounded-tl-3xl rounded-br-3xl shadow-sm break-words"
          style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
        >
          <span className="text-xs md:text-sm leading-relaxed whitespace-pre-wrap">
            {message.text}
          </span>
        </div>
      </div>
    );
  }

  const showFooter =
    message.status === "done" ||
    (message.status === "streaming" && message.webSources?.length);

  return (
    <div className="text-xs md:text-sm flex gap-2 md:gap-3 items-start mb-2 md:mb-4">
      <BotAvatarIcon />
      <div className="flex-1 min-w-0 mt-2">
        {message.status === "loading" && message.understoodQuery && (
          <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">
            {language === "zh" ? "理解為：" : "Understood as:"} "
            {message.understoodQuery}"
          </p>
        )}

        <div className="font-medium leading-relaxed text-black dark:text-white">
          {message.status === "loading" && (
            <StageIndicator
              stage={message.stage ?? "searching"}
              language={language}
            />
          )}
          {message.status !== "loading" && message.status !== "error" && (
            <AnswerText
              text={message.text || " "}
              webSources={message.webSources}
            />
          )}
          {message.status === "streaming" && <BlinkingCursor />}
          {message.status === "error" && (
            <span className="text-red-600 dark:text-red-400">
              {language === "zh" ? "發生錯誤：" : "Something went wrong:"}{" "}
              {message.errorText}
            </span>
          )}
        </div>
        <span className="inline-block mt-1 text-[10px] text-slate-400 dark:text-gray-500 tabular-nums">
          <ElapsedTimer
            startedAt={message.startedAt}
            elapsedMs={message.elapsedMs}
          />
        </span>

        {showFooter && (
          <div>
            <MessageFooter message={message} language={language} />
          </div>
        )}
      </div>
    </div>
  );
}

// Rewrite citation markers into markdown link syntax [1](url) and hand off to
// react-markdown for unified rendering, so bold/lists/etc. and citation links
// render correctly together instead of needing a hand-rolled splitting pass.
function AnswerText({ text, webSources }) {
  const withLinks =
    webSources && webSources.length > 0
      ? text.replace(/\[(\d+(?:\s*,\s*\d+)*)\]/g, (_match, numberList) =>
          numberList
            .split(",")
            .map((n) => n.trim())
            .map((n) => {
              const source = webSources.find((s) => s.index === Number(n));
              return source ? `[${n}](${source.url})` : `[${n}]`;
            })
            .join(" "),
        )
      : text;

  return (
    <div
      className="prose-chat
        [&_table]:border-collapse [&_table]:my-2
        [&_th]:border [&_th]:border-slate-300 dark:[&_th]:border-gray-600 [&_th]:bg-slate-100 dark:[&_th]:bg-gray-700 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left
        [&_td]:border [&_td]:border-slate-300 dark:[&_td]:border-gray-600 [&_td]:px-2 [&_td]:py-1
        [&_h1]:text-sm [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:first:mt-0
        [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:first:mt-0
        [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:first:mt-0
        [&_p]:my-1.5 [&_p]:first:mt-0
        [&_ul]:my-1.5 [&_ul]:pl-4 [&_ul]:list-disc [&_ul]:space-y-0.5
        [&_ol]:my-1.5 [&_ol]:pl-4 [&_ol]:list-decimal [&_ol]:space-y-0.5
        [&_li]:leading-relaxed
        [&_strong]:font-semibold [&_strong]:text-black dark:[&_strong]:text-white
        [&_.katex]:text-[1em]"
    >
      {/* singleTilde off: a spec range like `-25~+150° C` is not strikethrough. */}
      <ReactMarkdown
        remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center align-super text-[0.7em] font-medium text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 rounded px-1 mx-0.5 no-underline"
            >
              {children}
            </a>
          ),
        }}
      >
        {withLinks}
      </ReactMarkdown>
    </div>
  );
}

function MessageFooter({ message, language }) {
  return (
    <div className="mt-1.5 px-1 flex flex-col gap-1.5">
      <SourceBadge source={message.source} language={language} />
      {message.source === "db" &&
        message.matchedFaqs &&
        message.matchedFaqs.length > 0 && (
          <ul className="text-xs text-slate-500 dark:text-gray-400 space-y-0.5">
            {message.matchedFaqs.map((faq) => (
              <li key={faq.questionId}>
                <span className="text-slate-400 dark:text-gray-500">
                  [{faq.displayId}]
                </span>{" "}
                {faq.sourceUrl ? (
                  <a
                    href={faq.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange-600 dark:text-orange-400 hover:underline"
                  >
                    {faq.question}
                  </a>
                ) : (
                  <>
                    {faq.question}{" "}
                    <span className="text-slate-400 dark:text-gray-500 italic">
                      {language === "zh"
                        ? "（內部項目，無外部連結）"
                        : "(internal entry, no external URL)"}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      {(message.source === "web_search" ||
        message.source === "website_intro") &&
        message.webSources?.length > 0 && (
          <>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {message.source === "website_intro"
                ? language === "zh"
                  ? "AI 根據公司網站生成的答案 — 待人工審核。"
                  : "AI-generated answer from the company website — pending human review."
                : language === "zh"
                  ? "AI 根據網路搜尋生成的答案 — 待人工審核。"
                  : "AI-generated answer from web search — pending human review."}
            </p>
            <WebSourcesList
              text={message.text}
              webSources={message.webSources}
              language={language}
            />
          </>
        )}
    </div>
  );
}

const CITED_PREVIEW_COUNT = 3;

const CARD_ROW_CLASS = "flex gap-2 overflow-x-auto pb-1";

function WebSourcesList({ text, webSources, language }) {
  const [showMoreCited, setShowMoreCited] = useState(false);
  const [showOthers, setShowOthers] = useState(false);
  const citedIndexes = extractCitedIndexes(text);
  const cited = webSources.filter((s) => citedIndexes.has(s.index));
  const others = webSources.filter((s) => !citedIndexes.has(s.index));

  const citedPreview = cited.slice(0, CITED_PREVIEW_COUNT);
  const citedRest = cited.slice(CITED_PREVIEW_COUNT);

  return (
    <div className="flex flex-col gap-1.5">
      {citedPreview.length > 0 && (
        <div className={CARD_ROW_CLASS}>
          {citedPreview.map((s) => (
            <SourceCard key={s.index} source={s} language={language} />
          ))}
        </div>
      )}
      {citedRest.length > 0 && (
        <div>
          <button
            onClick={() => setShowMoreCited((v) => !v)}
            className="text-xs text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 underline-offset-2 hover:underline"
          >
            {showMoreCited
              ? language === "zh"
                ? "隱藏"
                : "Hide"
              : language === "zh"
                ? `顯示另外 ${citedRest.length} 個引用來源`
                : `Show ${citedRest.length} more cited source${citedRest.length > 1 ? "s" : ""}`}
          </button>
          {showMoreCited && (
            <div className={`${CARD_ROW_CLASS} mt-1.5`}>
              {citedRest.map((s) => (
                <SourceCard key={s.index} source={s} language={language} />
              ))}
            </div>
          )}
        </div>
      )}
      {others.length > 0 && (
        <div>
          <button
            onClick={() => setShowOthers((v) => !v)}
            className="text-xs text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 underline-offset-2 hover:underline"
          >
            {showOthers
              ? language === "zh"
                ? "隱藏"
                : "Hide"
              : language === "zh"
                ? `顯示另外 ${others.length} 個搜尋到（未引用）的來源`
                : `Show ${others.length} more searched (not cited) source${others.length > 1 ? "s" : ""}`}
          </button>
          {showOthers && (
            <div className={`${CARD_ROW_CLASS} mt-1.5`}>
              {others.map((s) => (
                <SourceCard key={s.index} source={s} language={language} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ENRICHMENT_HINT = {
  blocked: {
    en: "Source blocks automated preview",
    zh: "此來源封鎖了自動預覽",
  },
  not_attempted: {
    en: "Preview not fetched (too many sources this query)",
    zh: "未擷取預覽（本次搜尋來源過多）",
  },
};

function SourceCard({ source, language }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = source.image && !imageFailed;
  const hint =
    source.enrichmentStatus === "ok"
      ? null
      : ENRICHMENT_HINT[source.enrichmentStatus]?.[language];

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      className="flex flex-col shrink-0 w-48 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden hover:shadow-md transition-shadow"
    >
      {showImage && (
        <div className="h-24 bg-slate-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
          <img
            src={source.image ?? undefined}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        </div>
      )}
      <div className="p-2 flex flex-col gap-0.5">
        <span className="text-xs text-slate-400 dark:text-gray-500">
          [{source.index}] {source.domain}
        </span>
        <span className="text-sm font-medium text-slate-800 dark:text-gray-100 line-clamp-2">
          {source.title || source.domain}
        </span>
        {source.description && (
          <span className="text-xs text-slate-500 dark:text-gray-400 line-clamp-2">
            {source.description}
          </span>
        )}
        {hint && (
          <span className="text-xs text-slate-400 dark:text-gray-500 italic line-clamp-1">
            {hint}
          </span>
        )}
      </div>
    </a>
  );
}

function SourceBadge({ source, language }) {
  if (!source || source === "small_talk") return null;
  if (source === "db") {
    return (
      <span className="inline-flex items-center gap-1 w-fit text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
        {language === "zh" ? "FAQ 資料庫" : "FAQ database"}
      </span>
    );
  }
  if (source === "website_intro") {
    return (
      <span className="inline-flex items-center gap-1 w-fit text-xs font-medium text-sky-700 dark:text-sky-400 bg-sky-100 dark:bg-sky-900/30 px-2 py-0.5 rounded-full">
        {language === "zh" ? "公司網站" : "Company website"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 w-fit text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
      {language === "zh" ? "網路搜尋" : "Web search"}
    </span>
  );
}

function StageIndicator({ stage, language }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-500 dark:text-gray-400">
      <span className="flex gap-1">
        <Dot delay="0ms" />
        <Dot delay="150ms" />
        <Dot delay="300ms" />
      </span>
      {STAGE_LABEL[stage][language]}
    </span>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-gray-500 animate-bounce"
      style={{ animationDelay: delay }}
    />
  );
}

function BlinkingCursor() {
  return (
    <span className="inline-block w-1.5 h-4 ml-0.5 -mb-0.5 bg-slate-500 dark:bg-gray-400 animate-pulse" />
  );
}

// While waiting, ticks its own local state on an interval — deliberately not
// wired through the parent's messages state, so the running clock only
// re-renders this small span instead of the whole message list. Once the
// message is done, it just renders the final elapsedMs and stops ticking.
function ElapsedTimer({ startedAt, elapsedMs }) {
  const [liveMs, setLiveMs] = useState(() => Date.now() - startedAt);

  useEffect(() => {
    if (elapsedMs != null) return;
    const id = setInterval(() => setLiveMs(Date.now() - startedAt), 200);
    return () => clearInterval(id);
  }, [startedAt, elapsedMs]);

  const seconds = ((elapsedMs ?? liveMs) / 1000).toFixed(1);
  return <span className="tabular-nums">{seconds}s</span>;
}
